import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "mx-3" }
const _hoisted_2 = { class: "gorda-vehicles-card" }
const _hoisted_3 = { class: "gorda-vehicles-toolbar" }
const _hoisted_4 = { class: "gorda-vehicles-toolbar__title" }
const _hoisted_5 = { class: "gorda-vehicles-search" }
const _hoisted_6 = ["placeholder"]
const _hoisted_7 = { class: "gorda-vehicles-pills" }
const _hoisted_8 = ["title", "aria-label"]
const _hoisted_9 = { class: "table-responsive p-0" }
const _hoisted_10 = {
  key: 0,
  class: "gorda-table-loading-indicator text-secondary text-xs"
}
const _hoisted_11 = { class: "gorda-th" }
const _hoisted_12 = { class: "gorda-th" }
const _hoisted_13 = { class: "gorda-th text-center" }
const _hoisted_14 = { class: "gorda-th text-center" }
const _hoisted_15 = { class: "gorda-th text-center" }
const _hoisted_16 = { class: "d-flex align-items-center gap-2" }
const _hoisted_17 = { class: "gorda-brand" }
const _hoisted_18 = { class: "gorda-model" }
const _hoisted_19 = { class: "d-flex align-items-center gap-2" }
const _hoisted_20 = { class: "gorda-color-name" }
const _hoisted_21 = { class: "text-center" }
const _hoisted_22 = { class: "text-center" }
const _hoisted_23 = { class: "text-center" }
const _hoisted_24 = { class: "gorda-created" }
const _hoisted_25 = { class: "text-center" }
const _hoisted_26 = { class: "px-3 pb-2" }

import { ref, computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import VehicleRepository from '@/repositories/VehicleRepository'
import ToastService from '@/services/ToastService'
import i18n from '@/plugins/i18n'
import PagePaginator from '@/components/PagePaginator.vue'
import VehicleCreateModal from '@/components/vehicles/VehicleCreateModal.vue'
import { Vehicle } from '@/types/Vehicle'
import dayjs from 'dayjs'

// ── Constants ──────────────────────────────────────────────────────────────────

const DEFAULT_SORT = 'plate'
const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 30

// ── Router / route ─────────────────────────────────────────────────────────────


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const route = useRoute()
const router = useRouter()

// ── URL-derived state ──────────────────────────────────────────────────────────

function parseSort(raw: string | undefined): { field: string; dir: 'asc' | 'desc' } {
  if (!raw) return { field: DEFAULT_SORT, dir: 'asc' }
  if (raw.startsWith('-')) return { field: raw.slice(1), dir: 'desc' }
  return { field: raw, dir: 'asc' }
}

const initialSort = parseSort(route.query.sort as string | undefined)
const sortField = ref<string>(initialSort.field)
const sortDir = ref<'asc' | 'desc'>(initialSort.dir)
const search = ref<string>((route.query.search as string) || '')
const page = ref<number>(route.query.page ? Math.max(1, Number(route.query.page)) : DEFAULT_PAGE)
const perPage = ref<number>(route.query.perPage ? Number(route.query.perPage) : DEFAULT_PER_PAGE)
const enabledFilter = ref<boolean | null>(
  route.query.enabled === 'true' ? true : route.query.enabled === 'false' ? false : null
)

// ── Debounce ───────────────────────────────────────────────────────────────────

const committedSearch = ref<string>(search.value)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

function flushSearch(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  committedSearch.value = search.value
}

function onSearchInput(): void {
  if (debounceTimer !== null) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    committedSearch.value = search.value
    debounceTimer = null
    page.value = 1
    commitUrlState()
  }, 300)
}

// ── Sort ───────────────────────────────────────────────────────────────────────

const sortParam = computed<string>(() =>
  sortDir.value === 'desc' ? `-${sortField.value}` : sortField.value
)

function setSort(field: string): void {
  flushSearch()
  if (sortField.value === field) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDir.value = 'asc'
  }
  page.value = 1
  commitUrlState()
}

function sortIconClass(field: string): string[] {
  if (sortField.value !== field) return ['fas', 'fa-sort']
  return sortDir.value === 'asc'
    ? ['fas', 'fa-sort-up', 'gorda-sort-icon--active']
    : ['fas', 'fa-sort-down', 'gorda-sort-icon--active']
}

// ── Enabled filter ─────────────────────────────────────────────────────────────

function setEnabledFilter(value: boolean | null): void {
  flushSearch()
  enabledFilter.value = value
  page.value = 1
  commitUrlState()
}

// ── URL state commit ───────────────────────────────────────────────────────────

function commitUrlState(): void {
  const q: Record<string, string | undefined> = {}
  if (committedSearch.value !== '') q.search = committedSearch.value
  if (enabledFilter.value !== null) q.enabled = String(enabledFilter.value)
  if (sortParam.value !== DEFAULT_SORT) q.sort = sortParam.value
  if (page.value !== DEFAULT_PAGE) q.page = String(page.value)
  if (perPage.value !== DEFAULT_PER_PAGE) q.perPage = String(perPage.value)
  router.replace({ query: q })
}

// ── Paginator handlers ─────────────────────────────────────────────────────────

function onPageUpdate(value: number): void {
  flushSearch()
  page.value = value
  commitUrlState()
}

function onPerPageUpdate(value: number): void {
  flushSearch()
  perPage.value = value
  page.value = 1
  commitUrlState()
}

// ── Data state ─────────────────────────────────────────────────────────────────

const rows = ref<Vehicle[]>([])
const total = ref<number>(0)
const loading = ref<boolean>(false)
const reloadNonce = ref<number>(0)

watchEffect(async () => {
  void committedSearch.value
  void enabledFilter.value
  void sortParam.value
  void page.value
  void perPage.value
  void reloadNonce.value

  const _search = committedSearch.value
  const _enabled = enabledFilter.value
  const _sort = sortParam.value
  const _page = page.value
  const _perPage = perPage.value

  loading.value = true
  try {
    const result = await VehicleRepository.list({
      search: _search || undefined,
      enabled: _enabled !== null ? _enabled : undefined,
      sort: _sort,
      page: _page,
      perPage: _perPage,
    })
    rows.value = result.vehicles
    total.value = result.total

    if (result.total > 0 && (_page - 1) * _perPage >= result.total) {
      page.value = 1
      commitUrlState()
    }
  } catch (e: unknown) {
    const err = e as { message?: string }
    ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), err?.message)
  } finally {
    loading.value = false
  }
})

// ── Create vehicle ─────────────────────────────────────────────────────────────

const createModalRef = ref<InstanceType<typeof VehicleCreateModal> | null>(null)

function onVehicleCreated(vehicleId: string): void {
  void vehicleId
  page.value = 1
  reloadNonce.value++
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(value: string): string {
  if (!value) return ''
  return dayjs(value).format('YYYY-MM-DD')
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h6", _hoisted_4, _toDisplayString(_ctx.$t('routes.vehicles')) + " · " + _toDisplayString(total.value), 1),
        _createElementVNode("div", _hoisted_5, [
          _cache[8] || (_cache[8] = _createElementVNode("em", { class: "fas fa-magnifying-glass" }, null, -1)),
          _withDirectives(_createElementVNode("input", {
            type: "search",
            placeholder: _ctx.$t('vehicles.placeholders.search'),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            onInput: onSearchInput,
            autocomplete: "off"
          }, null, 40, _hoisted_6), [
            [_vModelText, search.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["gorda-pill gorda-pill--neutral", { 'gorda-pill--active': enabledFilter.value === null }]),
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setEnabledFilter(null)))
          }, _toDisplayString(_ctx.$t('common.placeholders.all')), 3),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["gorda-pill gorda-pill--success", { 'gorda-pill--active': enabledFilter.value === true }]),
            onClick: _cache[2] || (_cache[2] = ($event: any) => (setEnabledFilter(true)))
          }, _toDisplayString(_ctx.$t('common.fields.enabled')), 3),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["gorda-pill gorda-pill--danger", { 'gorda-pill--active': enabledFilter.value === false }]),
            onClick: _cache[3] || (_cache[3] = ($event: any) => (setEnabledFilter(false)))
          }, _toDisplayString(_ctx.$t('common.fields.disabled')), 3)
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "gorda-vehicles-add-btn",
          title: _ctx.$t('vehicles.actions.add_vehicle'),
          "aria-label": _ctx.$t('vehicles.actions.add_vehicle'),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (createModalRef.value?.open()))
        }, [...(_cache[9] || (_cache[9] = [
          _createElementVNode("em", { class: "fas fa-plus" }, null, -1)
        ]))], 8, _hoisted_8)
      ]),
      _createElementVNode("div", _hoisted_9, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _cache[10] || (_cache[10] = _createElementVNode("em", { class: "fas fa-circle-notch fa-spin me-1" }, null, -1)),
              _createTextVNode(_toDisplayString(_ctx.$t('common.messages.waiting')), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("table", {
          class: _normalizeClass(["table align-items-center mb-0 gorda-vehicles-table", { 'gorda-table-body--loading': loading.value }])
        }, [
          _cache[13] || (_cache[13] = _createElementVNode("caption", { hidden: "" }, null, -1)),
          _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th", {
                class: "gorda-th cursor-pointer user-select-none",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (setSort('plate')))
              }, [
                _createTextVNode(_toDisplayString(_ctx.$t('drivers.fields.plate')) + " ", 1),
                _createElementVNode("em", {
                  class: _normalizeClass(["gorda-sort-icon", sortIconClass('plate')])
                }, null, 2)
              ]),
              _createElementVNode("th", {
                class: "gorda-th cursor-pointer user-select-none",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (setSort('brand')))
              }, [
                _createTextVNode(_toDisplayString(_ctx.$t('vehicles.fields.brand')) + " ", 1),
                _createElementVNode("em", {
                  class: _normalizeClass(["gorda-sort-icon", sortIconClass('brand')])
                }, null, 2)
              ]),
              _createElementVNode("th", _hoisted_11, _toDisplayString(_ctx.$t('vehicles.fields.model')), 1),
              _createElementVNode("th", _hoisted_12, _toDisplayString(_ctx.$t('vehicles.fields.color')), 1),
              _createElementVNode("th", _hoisted_13, _toDisplayString(_ctx.$t('common.fields.status')), 1),
              _createElementVNode("th", _hoisted_14, _toDisplayString(_ctx.$t('vehicles.fields.linked_drivers')), 1),
              _createElementVNode("th", {
                class: "gorda-th text-center cursor-pointer user-select-none",
                onClick: _cache[7] || (_cache[7] = ($event: any) => (setSort('created_at')))
              }, [
                _createTextVNode(_toDisplayString(_ctx.$t('common.fields.createdAt')) + " ", 1),
                _createElementVNode("em", {
                  class: _normalizeClass(["gorda-sort-icon", sortIconClass('created_at')])
                }, null, 2)
              ]),
              _createElementVNode("th", _hoisted_15, _toDisplayString(_ctx.$t('common.actions.edit')), 1)
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (vehicle) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: vehicle.id,
                class: "gorda-vehicles-row"
              }, [
                _createElementVNode("td", null, [
                  _createElementVNode("div", _hoisted_16, [
                    _cache[11] || (_cache[11] = _createElementVNode("span", { class: "gorda-plate-icon" }, [
                      _createElementVNode("em", { class: "fas fa-car" })
                    ], -1)),
                    _createVNode(_component_router_link, {
                      to: { name: 'vehicles.detail', params: { id: vehicle.id } },
                      class: "gorda-plate-link"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(vehicle.plate), 1)
                      ]),
                      _: 2
                    }, 1032, ["to"])
                  ])
                ]),
                _createElementVNode("td", null, [
                  _createElementVNode("span", _hoisted_17, _toDisplayString(vehicle.brand), 1)
                ]),
                _createElementVNode("td", null, [
                  _createElementVNode("span", _hoisted_18, _toDisplayString(vehicle.model), 1)
                ]),
                _createElementVNode("td", null, [
                  _createElementVNode("div", _hoisted_19, [
                    (vehicle.color?.hex)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: "gorda-color-dot",
                          style: _normalizeStyle({ backgroundColor: vehicle.color.hex })
                        }, null, 4))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_20, _toDisplayString(vehicle.color?.name ?? '—'), 1)
                  ])
                ]),
                _createElementVNode("td", _hoisted_21, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["gorda-status-badge", vehicle.enabled ? 'gorda-status-badge--solid-success' : 'gorda-status-badge--solid-danger'])
                  }, _toDisplayString(_ctx.$t(vehicle.enabled ? 'common.fields.enabled' : 'common.fields.disabled')), 3)
                ]),
                _createElementVNode("td", _hoisted_22, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["gorda-linked-count", { 'gorda-linked-count--active': (vehicle.linked_drivers_count ?? 0) > 0 }])
                  }, _toDisplayString(vehicle.linked_drivers_count ?? 0), 3)
                ]),
                _createElementVNode("td", _hoisted_23, [
                  _createElementVNode("span", _hoisted_24, _toDisplayString(vehicle.created_at ? formatDate(vehicle.created_at) : ''), 1)
                ]),
                _createElementVNode("td", _hoisted_25, [
                  _createVNode(_component_router_link, {
                    to: { name: 'vehicles.edit', params: { id: vehicle.id } },
                    class: "gorda-edit-btn",
                    title: _ctx.$t('common.actions.edit')
                  }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createElementVNode("em", { class: "fas fa-pencil" }, null, -1)
                    ]))]),
                    _: 1
                  }, 8, ["to", "title"])
                ])
              ]))
            }), 128))
          ])
        ], 2),
        _createElementVNode("div", _hoisted_26, [
          _createVNode(PagePaginator, {
            total: total.value,
            page: page.value,
            "per-page": perPage.value,
            "onUpdate:page": onPageUpdate,
            "onUpdate:perPage": onPerPageUpdate
          }, null, 8, ["total", "page", "per-page"])
        ])
      ])
    ]),
    _createVNode(VehicleCreateModal, {
      ref_key: "createModalRef",
      ref: createModalRef,
      onCreated: onVehicleCreated
    }, null, 512)
  ]))
}
}

})